import React, { useState, useEffect, useRef } from 'react';
import { IoSaveSharp, IoAddCircleOutline, IoTrashOutline, IoCloudUploadOutline, IoCheckmarkCircle, IoAlertCircle, IoDocumentTextOutline } from "react-icons/io5";
import * as XLSX from 'xlsx';

const API_BASE = "https://wa-api.globebot.io/auth";
const SERVICE_TYPE = "Whatsapp";

const CHANNEL_TYPES = ["Utility", "Authentication", "Marketing"];

// ── Country name → ISO code mapping (for Excel import) ────────────────────────
const COUNTRY_NAME_TO_CODE = {
    "argentina": "AR", "brazil": "BR", "chile": "CL", "colombia": "CO",
    "egypt": "EG", "france": "FR", "germany": "DE", "india": "IN",
    "indonesia": "ID", "israel": "IL", "italy": "IT", "malaysia": "MY",
    "mexico": "MX", "netherlands": "NL", "nigeria": "NG", "pakistan": "PK",
    "peru": "PE", "russia": "RU", "saudi arabia": "SA", "south africa": "ZA",
    "spain": "ES", "turkey": "TR", "united arab emirates": "AE", "uae": "AE",
    "united kingdom": "GB", "uk": "GB", "canada": "CA", "america": "US",
    "united states": "US", "usa": "US",
    // Africa
    "algeria": "DZ", "angola": "AO", "benin": "BJ", "botswana": "BW",
    "burkina faso": "BF", "burundi": "BI", "cameroon": "CM", "chad": "TD",
    "republic of the congo (brazzaville)": "CG", "eritrea": "ER", "ethiopia": "ET",
    "gabon": "GA", "gambia": "GM", "ghana": "GH", "guinea-bissau": "GW",
    "ivory coast": "CI", "kenya": "KE", "lesotho": "LS", "liberia": "LR",
    "libya": "LY", "madagascar": "MG", "malawi": "MW", "mali": "ML",
    "mauritania": "MR", "morocco": "MA", "mozambique": "MZ", "namibia": "NA",
    "niger": "NE", "rwanda": "RW", "senegal": "SN", "sierra leone": "SL",
    "somalia": "SO", "south sudan": "SS", "sudan": "SD", "swaziland": "SZ",
    "tanzania": "TZ", "togo": "TG", "tunisia": "TN", "uganda": "UG", "zambia": "ZM",
    // Asia Pacific
    "afghanistan": "AF", "australia": "AU", "bangladesh": "BD", "cambodia": "KH",
    "china": "CN", "hong kong": "HK", "japan": "JP", "laos": "LA",
    "mongolia": "MN", "nepal": "NP", "new zealand": "NZ", "papua new guinea": "PG",
    "philippines": "PH", "singapore": "SG", "sri lanka": "LK", "taiwan": "TW",
    "tajikistan": "TJ", "thailand": "TH", "turkmenistan": "TM", "uzbekistan": "UZ",
    "vietnam": "VN",
    // Eastern Europe
    "albania": "AL", "armenia": "AM", "azerbaijan": "AZ", "belarus": "BY",
    "bulgaria": "BG", "croatia": "HR", "czech republic": "CZ", "georgia": "GE",
    "greece": "GR", "hungary": "HU", "latvia": "LV", "lithuania": "LT",
    "moldova": "MD", "north macedonia": "MK", "poland": "PL", "romania": "RO",
    "serbia": "RS", "slovakia": "SK", "slovenia": "SI", "ukraine": "UA",
    // Latin America
    "bolivia": "BO", "costa rica": "CR", "dominican republic": "DO", "ecuador": "EC",
    "el salvador": "SV", "guatemala": "GT", "haiti": "HT", "honduras": "HN",
    "jamaica": "JM", "nicaragua": "NI", "panama": "PA", "paraguay": "PY",
    "puerto rico": "PR", "uruguay": "UY", "venezuela": "VE",
    // Middle East
    "bahrain": "BH", "iraq": "IQ", "jordan": "JO", "kuwait": "KW",
    "lebanon": "LB", "oman": "OM", "qatar": "QA", "yemen": "YE",
    // Western Europe
    "austria": "AT", "belgium": "BE", "denmark": "DK", "finland": "FI",
    "ireland": "IE", "norway": "NO", "portugal": "PT", "sweden": "SE",
    "switzerland": "CH",
};

const CODE_TO_COUNTRY_NAME = Object.fromEntries(
    Object.entries(COUNTRY_NAME_TO_CODE).map(([name, code]) => [code, name])
);

const ALL_COUNTRIES = [
    { code: "IN",  label: "(91) India" },
    { code: "US",  label: "(1) United States" },
    { code: "GB",  label: "(44) United Kingdom" },
    { code: "AE",  label: "(971) UAE" },
    { code: "SA",  label: "(966) Saudi Arabia" },
    { code: "SG",  label: "(65) Singapore" },
    { code: "AU",  label: "(61) Australia" },
    { code: "CA",  label: "(1) Canada" },
    { code: "DE",  label: "(49) Germany" },
    { code: "FR",  label: "(33) France" },
    { code: "BR",  label: "(55) Brazil" },
    { code: "NG",  label: "(234) Nigeria" },
    { code: "ZA",  label: "(27) South Africa" },
    { code: "PK",  label: "(92) Pakistan" },
    { code: "BD",  label: "(880) Bangladesh" },
    { code: "ID",  label: "(62) Indonesia" },
    { code: "MY",  label: "(60) Malaysia" },
    { code: "PH",  label: "(63) Philippines" },
    { code: "TH",  label: "(66) Thailand" },
    { code: "TR",  label: "(90) Turkey" },
    // Extended list for full Excel coverage
    { code: "AR",  label: "(54) Argentina" },
    { code: "CL",  label: "(56) Chile" },
    { code: "CO",  label: "(57) Colombia" },
    { code: "EG",  label: "(20) Egypt" },
    { code: "IL",  label: "(972) Israel" },
    { code: "IT",  label: "(39) Italy" },
    { code: "MX",  label: "(52) Mexico" },
    { code: "NL",  label: "(31) Netherlands" },
    { code: "PE",  label: "(51) Peru" },
    { code: "RU",  label: "(7) Russia" },
    { code: "ES",  label: "(34) Spain" },
];

// ── JWT helpers ───────────────────────────────────────────────────────────────
function decodeToken(token) {
    if (!token) return {};
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
        );
        return JSON.parse(jsonPayload);
    } catch (e) { return {}; }
}

function getUserIdFromToken() {
    if (typeof window !== 'undefined' && typeof window.locatorgarsotrga === 'function') {
        const rawToken = window.locatorgarsotrga("token");
        if (rawToken) {
            const decoded = decodeToken(rawToken);
            return { userId: decoded.id || decoded.userId || decoded._id, token: rawToken };
        }
    }
    const rawToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (rawToken) {
        const decoded = decodeToken(rawToken);
        return { userId: decoded.id || decoded.userId || decoded._id, token: rawToken };
    }
    return { userId: null, token: null };
}

// ── Rate computation ──────────────────────────────────────────────────────────
function computeRate(baseRate, marginType, marginValue, percentValue, fixedValue) {
    if (marginType === "FixedAmount") {
        const base = Number(baseRate) || 0;
        const v = Number((base + Number(fixedValue)).toFixed(4));
        return isNaN(v) || v <= 0 ? null : v;
    }
    if (baseRate === null || baseRate === undefined || baseRate === '') return null;
    const base = Number(baseRate);
    if (marginType === "MarginOnPrice") return Number((base + Number(marginValue)).toFixed(4));
    if (marginType === "PercentOnPrice") return Number((base + (base * Number(percentValue) / 100)).toFixed(4));
    return null;
}

// ── Row builder ───────────────────────────────────────────────────────────────
function buildRow(code, countryLabel, channel, assignRec, rec) {
    return {
        country: code,
        countryInfo: countryLabel,
        channel_type: channel,
        myRatePerUnit: assignRec ? Number(assignRec.My_Rate) : null,
        myRatePerUnitOriginal: assignRec ? Number(assignRec.My_Rate) : null,
        sellingRatePerUnit: rec ? Number(rec.My_Rate) : null,
        assignRecId: assignRec ? assignRec.id : null,
        id: rec ? rec.id : null,
    };
}

function getCountryLabel(code) {
    const found = ALL_COUNTRIES.find(c => c.code === code);
    if (found) return found.label;
    const name = CODE_TO_COUNTRY_NAME[code];
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : code;
}

// ── Upload status badge ───────────────────────────────────────────────────────
function UploadStatusBadge({ status }) {
    if (!status) return null;
    const isSuccess = status.type === 'success';
    const isError = status.type === 'error';
    const isLoading = status.type === 'loading';
    return (
        <div className={`flex items-center gap-2 text-[11px] font-semibold px-3 py-1.5 rounded-lg border
            ${isSuccess ? 'bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-700 dark:text-green-400' : ''}
            ${isError ? 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-700 dark:text-red-400' : ''}
            ${isLoading ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-400' : ''}
        `}>
            {isSuccess && <IoCheckmarkCircle size={14} />}
            {isError && <IoAlertCircle size={14} />}
            {isLoading && <span className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />}
            <span>{status.message}</span>
        </div>
    );
}

// ── Excel Upload Panel (super_admin only) ─────────────────────────────────────
function ExcelUploadPanel({ current_user_id, onUploadComplete }) {
    const [uploadStatus, setUploadStatus] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef();

    function parseExcel(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const wb = XLSX.read(e.target.result, { type: 'binary' });
                    const sheet = wb.Sheets[wb.SheetNames[0]];
                    const rows = XLSX.utils.sheet_to_json(sheet, { defval: null });
                    resolve(rows);
                } catch (err) { reject(err); }
            };
            reader.onerror = reject;
            reader.readAsBinaryString(file);
        });
    }

    function normalizeRows(rows) {
        // Forward-fill Market column (grouped rows in Excel)
        let lastMarket = null;
        return rows.map(row => {
            const country = row['Country']?.toString().trim();
            const marketing = parseFloat(row['Marketing']);
            const utility = parseFloat(row['Utility']);
            const authentication = parseFloat(row['Authentication']);

            // skip blank rows
            if (!country || isNaN(marketing)) return null;

            const code = COUNTRY_NAME_TO_CODE[country.toLowerCase()];
            if (!code) return null; // skip if no code mapping

            return { country: code, countryName: country, marketing, utility, authentication };
        }).filter(Boolean);
    }

    async function handleFile(file) {
        if (!file) return;
        if (!file.name.match(/\.(xlsx|xls)$/i)) {
            setUploadStatus({ type: 'error', message: 'Please upload a valid .xlsx or .xls file' });
            return;
        }
        setUploadStatus({ type: 'loading', message: 'Parsing file...' });
        try {
            const rows = await parseExcel(file);
            const normalized = normalizeRows(rows);
            if (normalized.length === 0) {
                setUploadStatus({ type: 'error', message: 'No valid country data found in file' });
                return;
            }
            setPreviewData(normalized);
            setUploadStatus({ type: 'success', message: `${normalized.length} countries parsed. Review and confirm.` });
        } catch (err) {
            setUploadStatus({ type: 'error', message: 'Failed to parse Excel file' });
        }
    }

    function handleDrop(e) {
        e.preventDefault();
        setDragging(false);
        handleFile(e.dataTransfer.files[0]);
    }

    async function handleConfirmUpload() {
        if (!previewData?.length) return;
        setUploading(true);
        setUploadStatus({ type: 'loading', message: `Uploading ${previewData.length * 3} rate records...` });

        let success = 0, failed = 0;

        // Fetch existing rates for current_user_id so we can PUT vs POST
        let existingRates = [];
        try {
            existingRates = await fetch(
                `${API_BASE}/user_rates?user_id=${current_user_id}&service_type=${SERVICE_TYPE}`
            ).then(r => r.json()).catch(() => []);
        } catch { /* ignore */ }

        for (const row of previewData) {
            const channels = [
                { channel_type: "Marketing",      rate: row.marketing },
                { channel_type: "Utility",         rate: row.utility },
                { channel_type: "Authentication",  rate: row.authentication },
            ];

            for (const ch of channels) {
                if (isNaN(ch.rate) || ch.rate <= 0) continue;
                const existing = existingRates.find(
                    r => r.country === row.country && r.channel_type === ch.channel_type
                );
                const body = {
                    user_id: current_user_id,
                    assign_by: current_user_id,
                    service_type: SERVICE_TYPE,
                    country: row.country,
                    channel_type: ch.channel_type,
                    My_Rate: ch.rate,
                    Selling_Rate: ch.rate,
                };
                try {
                    if (existing) {
                        await fetch(`${API_BASE}/user_rates/${existing.id}`, {
                            method: "PUT",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ My_Rate: ch.rate }),
                        });
                    } else {
                        await fetch(`${API_BASE}/user_rates`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify(body),
                        });
                    }
                    success++;
                } catch {
                    failed++;
                }
            }
        }

        setUploading(false);
        if (failed === 0) {
            setUploadStatus({ type: 'success', message: `✓ ${success} rates uploaded successfully` });
        } else {
            setUploadStatus({ type: 'error', message: `${success} uploaded, ${failed} failed` });
        }
        setPreviewData(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        onUploadComplete?.();
    }

    function handleCancel() {
        setPreviewData(null);
        setUploadStatus(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }

    return (
        <div className="border border-[#dee2e6] rounded-lg overflow-hidden dark:border-[#2a3441]">
            {/* Header */}
            <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/10 border-b border-amber-200 dark:border-amber-800 px-3 py-2">
                <IoDocumentTextOutline size={14} className="text-amber-600 dark:text-amber-400" />
                <span className="text-[11px] font-semibold text-amber-700 dark:text-amber-400">
                    Bulk Upload Rates via Excel
                </span>
                <span className="ml-auto text-[10px] text-amber-500 font-medium">Super Admin Only</span>
            </div>

            <div className="p-3 flex flex-col gap-3">
                {/* Drop Zone */}
                {!previewData && (
                    <div
                        onDragOver={e => { e.preventDefault(); setDragging(true); }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg py-5 cursor-pointer transition-colors
                            ${dragging
                                ? 'border-[#3f7fca] bg-blue-50 dark:bg-blue-900/10'
                                : 'border-[#d4cfcf] hover:border-[#3f7fca] hover:bg-[#f6f9fc] dark:border-[#2a3441] dark:hover:border-[#3f7fca]'
                            }`}
                    >
                        <IoCloudUploadOutline size={28} className="text-[#3f7fca]" />
                        <span className="text-[11px] font-semibold text-[#576071] dark:text-[#aaa]">
                            Drag & drop your Excel file here
                        </span>
                        <span className="text-[10px] text-[#858c97]">
                            Supports .xlsx/.xls with columns: Country, Marketing, Utility, Authentication
                        </span>
                        <span className="text-[10px] text-[#3f7fca] border border-[#3f7fca] rounded px-2 py-0.5 mt-1">
                            Browse File
                        </span>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls"
                            className="hidden"
                            onChange={e => handleFile(e.target.files[0])}
                        />
                    </div>
                )}

                {/* Status */}
                {uploadStatus && <UploadStatusBadge status={uploadStatus} />}

                {/* Preview Table */}
                {previewData && previewData.length > 0 && (
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                            <span className="text-[11px] font-semibold text-[#576071] dark:text-[#ccc]">
                                Preview — {previewData.length} countries
                            </span>
                            <span className="text-[10px] text-[#858c97]">Rates are per 1000 messages (paisa/unit)</span>
                        </div>
                        <div className="max-h-48 overflow-y-auto border border-[#dee2e6] rounded dark:border-[#2a3441]">
                            <table className="w-full text-[10px]">
                                <thead className="sticky top-0 bg-[#f6f9fc] dark:bg-[#1a1f27]">
                                    <tr>
                                        <th className="p-1 border-b border-[#dee2e6] dark:border-[#2a3441] text-left text-[#576071] dark:text-[#aaa] font-semibold">Country</th>
                                        <th className="p-1 border-b border-[#dee2e6] dark:border-[#2a3441] text-right text-[#576071] dark:text-[#aaa] font-semibold">Marketing</th>
                                        <th className="p-1 border-b border-[#dee2e6] dark:border-[#2a3441] text-right text-[#576071] dark:text-[#aaa] font-semibold">Utility</th>
                                        <th className="p-1 border-b border-[#dee2e6] dark:border-[#2a3441] text-right text-[#576071] dark:text-[#aaa] font-semibold">Authentication</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewData.map((row, i) => (
                                        <tr key={i} className={i % 2 === 0 ? 'bg-white dark:bg-[#121519]' : 'bg-[#fafafa] dark:bg-[#161b22]'}>
                                            <td className="p-1 text-[#576071] dark:text-[#ccc] font-medium">
                                                {row.countryName}
                                                <span className="ml-1 text-[#858c97] font-normal">({row.country})</span>
                                            </td>
                                            <td className="p-1 text-right text-[#3f7fca]">{row.marketing}</td>
                                            <td className="p-1 text-right text-[#37715d]">{row.utility}</td>
                                            <td className="p-1 text-right text-orange-500">{row.authentication}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Confirm / Cancel */}
                        <div className="flex gap-2 justify-end mt-1">
                            <button
                                onClick={handleCancel}
                                className="text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-[#d4cfcf] text-[#576071] hover:bg-[#f6f9fc] dark:border-[#2a3441] dark:text-[#aaa] dark:hover:bg-[#1a1f27] transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmUpload}
                                disabled={uploading}
                                className="flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-lg bg-[#3f7fca] text-white hover:bg-[#3270b5] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {uploading
                                    ? <><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Uploading...</>
                                    : <><IoCloudUploadOutline size={13} /> Confirm & Upload</>
                                }
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── My Rate Input ─────────────────────────────────────────────────────────────
function MyRateInput({ row, blockCountryCode, onBlurSave, onChange }) {
    return (
        <input
            type="number"
            value={row.myRatePerUnit ?? ''}
            step="0.0001"
            min="0"
            onChange={e => onChange(blockCountryCode, row.channel_type, e.target.value)}
            onBlur={() => onBlurSave(blockCountryCode, row)}
            className="w-[90px] text-[11px] font-semibold text-[#3f7fca] border border-[#3f7fca] rounded px-1 py-0.5 outline-none bg-transparent dark:bg-[#121519] dark:text-[#7eb3f5]"
        />
    );
}

// ── Main component ────────────────────────────────────────────────────────────
function ManageuserforWhatsapp({ user_id, current_user_id }) {
    const [countryBlocks, setCountryBlocks]     = useState([]);
    const [assignByRates, setAssignByRates]     = useState([]);

    const [isSuperAdmin, setIsSuperAdmin]       = useState(false);
    const [roleLoading, setRoleLoading]         = useState(true);

    const [marginType, setMarginType]           = useState('');
    const [marginonPrice, setMarginonPrice]     = useState(0.03);
    const [percentonPrice, setPercentonPrice]   = useState(3);
    const [fixedAmount, setFixedAmount]         = useState(0);

    const [loading, setLoading]                 = useState(true);
    const [submitting, setSubmitting]           = useState(false);
    const [selectedCountry, setSelectedCountry] = useState('');

    const { userId } = getUserIdFromToken();

    // ── Step 1: resolve role ──────────────────────────────────────────────────
    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await fetch(`${API_BASE}/user/${userId}`);
                const data = await response.json();
                setIsSuperAdmin(data.user.role === "super_admin");
            } catch (error) {
                console.error("Error fetching user:", error);
            } finally {
                setRoleLoading(false);
            }
        };
        if (userId) fetchUser();
        else setRoleLoading(false);
    }, [userId]);

    // ── Step 2: fetch rates ───────────────────────────────────────────────────
    const loadRates = async () => {
        setLoading(true);
        try {
            const [assignRates, userRates] = await Promise.all([
                fetch(`${API_BASE}/user_rates?user_id=${current_user_id}&service_type=${SERVICE_TYPE}`)
                    .then(r => r.json()).catch(() => []),
                fetch(`${API_BASE}/user_rates?user_id=${user_id}&service_type=${SERVICE_TYPE}`)
                    .then(r => r.json()).catch(() => []),
            ]);

            setAssignByRates(assignRates);

            // ── Merge assign_by countries + user_id countries ─────────────────
            // Collect all country codes that should be shown:
            //   1. Countries already set for user_id
            //   2. Countries from assign_by (current_user_id) — so super admin's rates appear
            const countryCodes = new Set([
                ...userRates.map(r => r.country),
                ...assignRates.map(r => r.country),
            ]);

            if (countryCodes.size > 0) {
                const blocks = [];
                for (const code of countryCodes) {
                    const label = getCountryLabel(code);
                    const rows = CHANNEL_TYPES.map(channel => {
                        const rec       = userRates.find(r => r.country === code && r.channel_type === channel && r.service_type === SERVICE_TYPE);
                        const assignRec = assignRates.find(r => r.country === code && r.channel_type === channel);
                        return buildRow(code, label, channel, assignRec, rec);
                    });
                    blocks.push({ countryCode: code, countryLabel: label, rows });
                }
                setCountryBlocks(blocks);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (roleLoading) return;
        loadRates();
    }, [user_id, current_user_id, roleLoading]);

    // ── My Rate handlers ──────────────────────────────────────────────────────
    function handleMyRateChange(countryCode, channel_type, value) {
        setCountryBlocks(prev => prev.map(block => {
            if (block.countryCode !== countryCode) return block;
            return {
                ...block,
                rows: block.rows.map(row =>
                    row.channel_type === channel_type
                        ? { ...row, myRatePerUnit: value === '' ? null : Number(value) }
                        : row
                ),
            };
        }));
    }

    async function handleMyRateBlurSave(countryCode, row) {
        const newVal = Number(row.myRatePerUnit);
        if (isNaN(newVal) || newVal <= 0 || newVal === row.myRatePerUnitOriginal) return;

        const confirmed = window.confirm(
            `Update My Rate for ${row.countryInfo} - ${row.channel_type} from ${row.myRatePerUnitOriginal} to ${newVal}?`
        );
        if (!confirmed) {
            setCountryBlocks(prev => prev.map(block => {
                if (block.countryCode !== countryCode) return block;
                return {
                    ...block,
                    rows: block.rows.map(r =>
                        r.channel_type === row.channel_type
                            ? { ...r, myRatePerUnit: r.myRatePerUnitOriginal }
                            : r
                    ),
                };
            }));
            return;
        }

        try {
            const body = {
                My_Rate: newVal,
                service_type: SERVICE_TYPE,
                country: row.country,
                channel_type: row.channel_type,
            };
            if (row.assignRecId) {
                await fetch(`${API_BASE}/user_rates/${row.assignRecId}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                });
            } else {
                await fetch(`${API_BASE}/user_rates`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ ...body, user_id: current_user_id, assign_by: current_user_id, Selling_Rate: newVal }),
                });
            }
            setCountryBlocks(prev => prev.map(block => {
                if (block.countryCode !== countryCode) return block;
                return {
                    ...block,
                    rows: block.rows.map(r =>
                        r.channel_type === row.channel_type
                            ? { ...r, myRatePerUnitOriginal: newVal }
                            : r
                    ),
                };
            }));
            alert(`My Rate updated successfully for ${row.countryInfo} - ${row.channel_type}`);
        } catch {
            alert(`Failed to update My Rate for ${row.countryInfo} - ${row.channel_type}`);
            setCountryBlocks(prev => prev.map(block => {
                if (block.countryCode !== countryCode) return block;
                return {
                    ...block,
                    rows: block.rows.map(r =>
                        r.channel_type === row.channel_type
                            ? { ...r, myRatePerUnit: r.myRatePerUnitOriginal }
                            : r
                    ),
                };
            }));
        }
    }

    // ── Add / Remove country ──────────────────────────────────────────────────
    function handleAddCountry() {
        if (!selectedCountry) return;
        if (countryBlocks.find(b => b.countryCode === selectedCountry)) {
            alert('This country is already added.');
            return;
        }
        const meta = ALL_COUNTRIES.find(c => c.code === selectedCountry);
        const newRows = CHANNEL_TYPES.map(channel => {
            const assignRec = assignByRates.find(r => r.country === selectedCountry && r.channel_type === channel);
            return buildRow(selectedCountry, meta.label, channel, assignRec, null);
        });
        setCountryBlocks(prev => [...prev, { countryCode: selectedCountry, countryLabel: meta.label, rows: newRows }]);
        setSelectedCountry('');
    }

    function handleRemoveCountry(code) {
        setCountryBlocks(prev => prev.filter(b => b.countryCode !== code));
    }

    // ── Save All ──────────────────────────────────────────────────────────────
    async function handleSubmit() {
        if (!marginType) {
            alert('Please select a Margin Type first.');
            return;
        }
        setSubmitting(true);
        const allRows = countryBlocks.flatMap(b => b.rows);
        for (const row of allRows) {
            if (marginType !== "FixedAmount" && row.myRatePerUnit === null) continue;
            const computedRate = computeRate(row.myRatePerUnit, marginType, marginonPrice, percentonPrice, fixedAmount);
            if (computedRate === null) continue;
            const body = {
                user_id,
                assign_by: current_user_id,
                service_type: SERVICE_TYPE,
                country: row.country,
                channel_type: row.channel_type,
                My_Rate: computedRate,
            };
            try {
                if (row.id) {
                    await fetch(`${API_BASE}/user_rates/${row.id}`, {
                        method: "PUT",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(body),
                    });
                } else {
                    await fetch(`${API_BASE}/user_rates`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ ...body, Selling_Rate: computedRate }),
                    });
                }
            } catch {
                alert(`Error saving ${row.countryInfo} - ${row.channel_type}`);
            }
        }

        // Reload
        try {
            const [assignRates, userRates] = await Promise.all([
                fetch(`${API_BASE}/user_rates?user_id=${current_user_id}&service_type=${SERVICE_TYPE}`).then(r => r.json()),
                fetch(`${API_BASE}/user_rates?user_id=${user_id}&service_type=${SERVICE_TYPE}`).then(r => r.json()),
            ]);
            setAssignByRates(assignRates);
            setCountryBlocks(prev => prev.map(block => ({
                ...block,
                rows: block.rows.map(row => {
                    const rec       = userRates.find(r => r.country === row.country && r.channel_type === row.channel_type && r.service_type === SERVICE_TYPE);
                    const assignRec = assignRates.find(r => r.country === row.country && r.channel_type === row.channel_type);
                    return buildRow(row.country, row.countryInfo, row.channel_type, assignRec, rec);
                }),
            })));
            alert('Saved successfully!');
        } catch {
            alert('Saved but failed to reload data.');
        } finally {
            setSubmitting(false);
        }
    }

    // ── Render ────────────────────────────────────────────────────────────────
    const addedCodes         = countryBlocks.map(b => b.countryCode);
    const availableCountries = ALL_COUNTRIES.filter(c => !addedCodes.includes(c.code));

    if (roleLoading || loading) {
        return <div className="text-[12px] text-gray-500 p-4">Loading rates...</div>;
    }

    return (
        <div className='w-full flex flex-col gap-3'>

            {/* ── Excel Upload (super_admin only) ── */}
            {isSuperAdmin && (
                <ExcelUploadPanel
                    current_user_id={current_user_id}
                    onUploadComplete={loadRates}
                />
            )}

            {/* ── Margin Controls ── */}
            <div className='flex flex-wrap gap-3 items-end'>
                <div className='flex flex-col gap-1'>
                    <label className='text-[12px] font-semibold text-[#000] dark:text-[#fff]'>Margin Type</label>
                    <select
                        value={marginType}
                        onChange={e => setMarginType(e.target.value)}
                        className='text-[#454546] border border-[#d4cfcf] rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-[#fff]'
                    >
                        <option value="">Select Margin Type</option>
                        <option value="MarginOnPrice">Margin On Price</option>
                        <option value="PercentOnPrice">Percent On Price</option>
                        <option value="FixedAmount">Fixed Amount</option>
                    </select>
                </div>

                {marginType === "MarginOnPrice" && (
                    <div className='flex flex-col gap-1'>
                        <label className='text-[12px] font-semibold text-[#000] dark:text-[#fff]'>Margin Amount</label>
                        <input type="number" className='text-[#454546] border border-[#d4cfcf] rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-[#fff]'
                            value={marginonPrice} onChange={e => setMarginonPrice(Number(e.target.value))} step="0.01" min="0" />
                    </div>
                )}
                {marginType === "PercentOnPrice" && (
                    <div className='flex flex-col gap-1'>
                        <label className='text-[12px] font-semibold text-[#000] dark:text-[#fff]'>Percent (%) On Price</label>
                        <input type="number" className='text-[#454546] border border-[#d4cfcf] rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-[#fff]'
                            value={percentonPrice} onChange={e => setPercentonPrice(Number(e.target.value))} step="0.1" min="0" />
                    </div>
                )}
                {marginType === "FixedAmount" && (
                    <div className='flex flex-col gap-1'>
                        <label className='text-[12px] font-semibold text-[#000] dark:text-[#fff]'>Fixed Amount (added on top)</label>
                        <input type="number" className='text-[#454546] border border-[#d4cfcf] rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-[#fff]'
                            value={fixedAmount} onChange={e => setFixedAmount(Number(e.target.value))} step="0.0001" min="0" />
                        <span className='text-[10px] text-gray-400 max-w-[220px]'>Selling Rate = My Rate + Fixed Amount</span>
                    </div>
                )}
            </div>

            {/* ── Add Country ── */}
            <div className='flex gap-2 items-end'>
                <div className='flex flex-col gap-1'>
                    <label className='text-[12px] font-semibold text-[#000] dark:text-[#fff]'>Add Country</label>
                    <select
                        value={selectedCountry}
                        onChange={e => setSelectedCountry(e.target.value)}
                        className='text-[#454546] border border-[#d4cfcf] rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-[#fff]'
                    >
                        <option value="">Select Country</option>
                        {availableCountries.map(c => (
                            <option key={c.code} value={c.code}>{c.label}</option>
                        ))}
                    </select>
                </div>
                <button
                    onClick={handleAddCountry}
                    disabled={!selectedCountry}
                    className='flex items-center gap-1 border border-[#3f7fca] text-[#3f7fca] rounded-lg px-3 py-2 text-[12px] font-bold hover:bg-[#3f7fca] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
                >
                    <IoAddCircleOutline size={14} /> Add Country
                </button>
            </div>

            {/* ── Warning ── */}
            <div className='flex justify-end'>
                <span className='text-[#858c97] text-[12px]'>
                    Selling price cannot be set <span className='text-red-600'>less than 50%</span> of your cost price
                </span>
            </div>

            {/* ── Country Blocks ── */}
            {countryBlocks.length === 0 ? (
                <div className='text-center text-[12px] text-gray-400 py-6 border border-dashed border-[#dee2e6] rounded-lg'>
                    No countries added yet. Select a country above or upload an Excel sheet to get started.
                </div>
            ) : (
                countryBlocks.map(block => (
                    <div key={block.countryCode} className='border border-[#dee2e6] rounded-lg overflow-hidden dark:border-[#2a3441]'>

                        {/* Country Header */}
                        <div className='flex items-center justify-between bg-[#f6f9fc] dark:bg-[#1a1f27] px-3 py-2 border-b border-[#dee2e6] dark:border-[#2a3441]'>
                            <div className='flex items-center gap-2'>
                                <span className='text-[12px] font-semibold text-[#576071] dark:text-[#fff]'>
                                    {block.countryLabel}
                                </span>
                                {/* Badge if this country came from assign_by rates */}
                                {block.rows.some(r => r.assignRecId) && !block.rows.some(r => r.id) && (
                                    <span className='text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800'>
                                        From Your Rates
                                    </span>
                                )}
                            </div>
                            <button
                                onClick={() => handleRemoveCountry(block.countryCode)}
                                className='text-red-400 hover:text-red-600 transition-colors'
                                title='Remove country'
                            >
                                <IoTrashOutline size={14} />
                            </button>
                        </div>

                        {/* Rates Table */}
                        <table className='w-full'>
                            <thead>
                                <tr className='bg-[#f6f9fc] dark:bg-[#121519] text-left'>
                                    <th className='p-1 border border-[#dee2e6] dark:border-[#2a3441] text-[11px] text-[#576071] dark:text-[#aaa]'>Channel Type</th>
                                    <th className='p-1 border border-[#dee2e6] dark:border-[#2a3441] text-[11px] text-[#576071] dark:text-[#aaa]'>
                                        My Rate / Unit
                                        {isSuperAdmin && <span className='ml-1 text-amber-500 font-normal'>(editable)</span>}
                                    </th>
                                    <th className='p-1 border border-[#dee2e6] dark:border-[#2a3441] text-[11px] text-[#576071] dark:text-[#aaa]'>Selling Rate / Unit</th>
                                    <th className='p-1 border border-[#dee2e6] dark:border-[#2a3441] text-[11px] text-[#576071] dark:text-[#aaa]'>Preview Selling Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {block.rows.map((row, i) => {
                                    const previewRate = marginType
                                        ? computeRate(row.myRatePerUnit, marginType, marginonPrice, percentonPrice, fixedAmount)
                                        : null;

                                    return (
                                        <tr key={i} className='dark:bg-[#121519]'>
                                            <td className='p-1 text-[11px] border border-[#dee2e6] dark:border-[#2a3441] font-medium text-[#576071] dark:text-[#ccc]'>
                                                {row.channel_type}
                                            </td>
                                            <td className='p-1 text-[11px] border border-[#dee2e6] dark:border-[#2a3441]'>
                                                {isSuperAdmin ? (
                                                    <MyRateInput
                                                        row={row}
                                                        blockCountryCode={block.countryCode}
                                                        onChange={handleMyRateChange}
                                                        onBlurSave={handleMyRateBlurSave}
                                                    />
                                                ) : (
                                                    <span className='text-[#3f7fca]'>
                                                        {row.myRatePerUnit !== null
                                                            ? row.myRatePerUnit
                                                            : <span className='text-red-400'>N/A</span>}
                                                    </span>
                                                )}
                                            </td>
                                            <td className='p-1 text-[11px] border border-[#dee2e6] dark:border-[#2a3441] text-[#37715d]'>
                                                {row.sellingRatePerUnit !== null
                                                    ? row.sellingRatePerUnit
                                                    : <span className='text-gray-400'>-</span>}
                                            </td>
                                            <td className='p-1 text-[11px] border border-[#dee2e6] dark:border-[#2a3441]'>
                                                {previewRate !== null
                                                    ? <span className='text-orange-500 font-semibold'>{previewRate}</span>
                                                    : <span className='text-gray-400'>-</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ))
            )}

            {/* ── Save All ── */}
            {countryBlocks.length > 0 && (
                <div className='flex justify-end mt-1'>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className='flex items-center justify-center border border-[#37715d] rounded-lg px-4 py-2 gap-2 hover:bg-[#37715d] hover:text-white text-[#37715d] transition-colors disabled:opacity-50'
                    >
                        <span className='text-[12px] font-bold flex items-center gap-2'>
                            <IoSaveSharp /> {submitting ? "Saving..." : "Save All"}
                        </span>
                    </button>
                </div>
            )}
        </div>
    );
}

export default ManageuserforWhatsapp;